import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import type { VolunteerTask } from "../types";
import type { VolunteerStore } from "../store";

interface Props {
  store: VolunteerStore;
  task?: VolunteerTask;
  open: boolean;
  onClose: () => void;
}

export const TaskFormModal = observer(({ store, task, open, onClose }: Props) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        title: task?.title ?? "",
        description: task?.description ?? "",
        notesTitle: task?.notesTitle ?? "",
        order: task?.order ?? 0,
        subjectId: task?.subjectId,
        contactPersonName: task?.contactPersonName ?? "",
        contactPersonPhone: task?.contactPersonPhone ?? "",
      });
    }
  }, [open, task, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      if (task?.objectId) {
        await store.updateTask(task.objectId, {
          title: values.title,
          description: values.description || undefined,
          notesTitle: values.notesTitle || undefined,
          order: values.order,
          subjectId: values.subjectId || undefined,
          contactPersonName: values.contactPersonName || undefined,
          contactPersonPhone: values.contactPersonPhone || undefined,
        });
      } else {
        await store.createTask({
          title: values.title,
          description: values.description || undefined,
          notesTitle: values.notesTitle || undefined,
          order: values.order,
          subjectId: values.subjectId || undefined,
          contactPersonName: values.contactPersonName || undefined,
          contactPersonPhone: values.contactPersonPhone || undefined,
        });
      }
      form.resetFields();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.objectId) return;
    await store.deleteTask(task.objectId);
    onClose();
  };

  return (
    <Modal
      open={open}
      title={task ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
      okText={task ? "Speichern" : "Anlegen"}
      cancelText="Abbrechen"
      confirmLoading={submitting}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {task && (
            <Popconfirm
              title="Aufgabe wirklich löschen?"
              description="Alle Schichten und Anmeldungen dieser Aufgabe werden ebenfalls gelöscht."
              onConfirm={handleDelete}
              okText="Ja"
              cancelText="Nein"
            >
              <Button danger style={{ float: "left" }}>
                Löschen
              </Button>
            </Popconfirm>
          )}
          <CancelBtn />
          <OkBtn />
        </>
      )}
    >
      <Form form={form} layout="vertical" initialValues={{ order: 0 }}>
        <Form.Item
          name="title"
          label="Titel"
          rules={[{ required: true, message: "Bitte Titel eingeben" }]}
        >
          <Input placeholder="z.B. Buffet, Aufbau, Bar-Schicht" />
        </Form.Item>
        <Form.Item name="description" label="Beschreibung">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="subjectId" label="Thema">
          <Select
            allowClear
            placeholder="Kein Thema"
            options={store.activeSubjects.map((s) => ({ value: s.objectId, label: s.title }))}
          />
        </Form.Item>
        <Form.Item
          name="notesTitle"
          label="Notizfeld-Beschriftung (optional)"
          extra="Wenn gesetzt, können sich Helfer:innen zusätzlich mit einer öffentlich sichtbaren Notiz eintragen, z.B. 'Was bringst du mit?'"
        >
          <Input placeholder="z.B. Was bringst du mit?" />
        </Form.Item>
        <Form.Item name="contactPersonName" label="Ansprechperson (Name)">
          <Input placeholder="z.B. Max Mustermann" />
        </Form.Item>
        <Form.Item
          name="contactPersonPhone"
          label="Telefonnummer (nur für Admins sichtbar)"
          extra="Wird nur angezeigt, wenn du als Admin eingeloggt bist."
        >
          <Input placeholder="z.B. 0151 23456789" />
        </Form.Item>
        <Form.Item name="order" label="Reihenfolge">
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
});
